import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
  Tooltip,
  Button,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import styles from "./document-analyzer.module.scss";
import MarkdownRenderer from "../../MarkdownRenderer";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import { recordAgentAction } from "../../../store/agentSlice";

interface ClauseReference {
  clause_number?: string;
  section_reference: string;
  clause_text_snippet: string;
  clause_document_url?: string;
}

interface ContractDetails {
  contract_type: string;
  contracting_parties: string[];
  agreement_date: string;
}

interface DocumentAnalysis {
  document_name: string;
  contract_details: ContractDetails;
  is_coc_clause_present: boolean;
  clause_reference: ClauseReference[];
  extracted_threshold: string;
  transaction_percentage: string;
  trigger_result: string;
  reason: string;
  action_required: string | string[];
  consequence_summary: string;
  risk_tier: string;
  human_review: string;
}

interface ParsedContent {
  finalAnswer?: {
    documents?: DocumentAnalysis[];
  };
  question?: string;
}

interface DocumentAnalyzerAgentProps {
  parsedContent: ParsedContent;
  messageId?: string;
}

interface ClauseModalData {
  documentName: string;
  clauses: ClauseReference[];
}

interface DocumentFeedback {
  feedback_type: "Useful" | "Too generic" | "Incorrect" | "";
  feedback_comment: string;
}


const DOCUMENT_ANALYZER_ENDPOINT = "/stream/coc-review";

const getRiskTierColor = (riskTier: string): string => {
  const normalized = riskTier?.toLowerCase();
  if (normalized === "green" || normalized === "low") return "green";
  if (normalized === "red" || normalized === "high") return "red";
  if (normalized === "yellow" || normalized === "medium") return "medium";
  return "medium";
};

const formatContractingParties = (parties: string[]): string => {
  if (!parties || parties.length === 0) return "N/A";
  if (parties.length === 1) return parties[0];
  if (parties.length === 2) return `${parties[0]} and ${parties[1]}`;
  return `${parties.slice(0, -1).join(", ")}, and ${parties[parties.length - 1]}`;
};

export default function DocumentAnalyzerAgent({
  parsedContent,
  messageId,
}: DocumentAnalyzerAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const currentChat = useSelector((state: RootState) => state.user.currentChat);
  const numericAgentId = currentAgent?.id || 11;
  const documents = parsedContent?.finalAnswer?.documents || [];
  const [modalData, setModalData] = useState<ClauseModalData | null>(null);
  const [feedbackData, setFeedbackData] = useState<Record<number, DocumentFeedback>>({});
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const isLatestMessage =
    [...currentChat].reverse().find((m) => m.sender === "agent")?.id ===
    messageId;

  const handleClauseClick = (
    documentName: string,
    clauses: ClauseReference[],
  ) => {
    if (clauses && clauses.length > 0) {
      setModalData({ documentName, clauses });
    }
  };

  const handleCloseModal = () => {
    setModalData(null);
  };

  const handleFeedbackChange = (
    docIndex: number,
    field: "feedback_type" | "feedback_comment",
    value: string,
  ) => {
    setFeedbackData((prev) => ({
      ...prev,
      [docIndex]: {
        ...prev[docIndex],
        feedback_type: field === "feedback_type"
          ? (value as DocumentFeedback["feedback_type"])
          : prev[docIndex]?.feedback_type || "",
        feedback_comment: field === "feedback_comment"
          ? value
          : prev[docIndex]?.feedback_comment || "",
      },
    }));
  };


  const isFeedbackComplete = () => {
    // Feedback is optional, but if started, both type and comment must be provided
    // Returns true if:
    // 1. No feedback provided at all (all fields empty) - OK to proceed
    // 2. All started feedback has both type and comment filled - OK to proceed
    // Returns false if:
    // - Any feedback has only type OR only comment (incomplete) - NOT OK

    const feedbackValues = Object.values(feedbackData);

    // If no feedback data exists at all, allow proceeding
    if (feedbackValues.length === 0) {
      return true;
    }

    // Check each feedback entry
    return feedbackValues.every((feedback) => {
      if (!feedback) return true; // No feedback for this document

      const hasType = feedback.feedback_type && feedback.feedback_type.trim();
      const hasComment = feedback.feedback_comment && feedback.feedback_comment.trim();

      // Either both empty (no feedback started) or both filled (feedback complete)
      return (!hasType && !hasComment) || (hasType && hasComment);
    });
  };

  const handleConfirmation = (response: "yes" | "no") => {
    if (!currentAgent?.id) return;

    // Prepare feedback array for documents that have feedback
    const documentFeedback = documents.map((doc, index) => {
      const feedback = feedbackData[index];
      if (feedback && feedback.feedback_type && feedback.feedback_comment.trim()) {
        return {
          document_name: doc.document_name,
          feedback_type: feedback.feedback_type,
          feedback_comment: feedback.feedback_comment,
        };
      }
      return null;
    }).filter(Boolean);

    // Create message with response and feedback data
    const messagePayload = {
      response: response === "yes"
        ? "Yes, I am satisfied with this outcome."
        : "No, I would like to adjust something.",
      document_feedback: documentFeedback,
    };

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent.id,
        message: JSON.stringify(messagePayload),
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: DOCUMENT_ANALYZER_ENDPOINT,
        sessionId: null,
      }),
    );

    setFeedbackSubmitted(true);
  };

  if (documents.length === 0) {
    return (
      <div className={styles.container}>
        <p>No documents found for analysis.</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        <h2 className={styles.title}>Change of Control Analysis</h2>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Contract Details</th>
              <th>Identify CoC</th>
              <th>Clause Reference</th>
              <th>Identify CoC Trigger</th>
              <th>Action items</th>
              <th>Consequence of not adhering to CoC requirements</th>
              <th>Risk Tier</th>
              {isLatestMessage && <th>Feedback</th>}
            </tr>
          </thead>
          <tbody>
            {documents.map((doc, index) => (
              <tr key={index}>
                <td className={styles.documentName}>
                  {doc.contract_details ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <div>
                        <strong
                          style={{ color: "#00a888", fontSize: "0.85em" }}
                        >
                          Type:
                        </strong>{" "}
                        <span>{doc.contract_details.contract_type}</span>
                      </div>
                      <div>
                        <strong
                          style={{ color: "#00a888", fontSize: "0.85em" }}
                        >
                          Parties:
                        </strong>{" "}
                        <span>
                          {formatContractingParties(
                            doc.contract_details.contracting_parties,
                          )}
                        </span>
                      </div>
                      <div>
                        <strong
                          style={{ color: "#00a888", fontSize: "0.85em" }}
                        >
                          Date:
                        </strong>{" "}
                        <span>{doc.contract_details.agreement_date}</span>
                      </div>
                    </div>
                  ) : (
                    doc.document_name
                  )}
                </td>
                <td className={styles.centered}>
                  <span
                    className={
                      doc.is_coc_clause_present ? styles.yes : styles.no
                    }
                  >
                    {doc.is_coc_clause_present ? "Yes" : "No"}
                  </span>
                </td>
                <td className={styles.clauseReference}>
                  {doc.clause_reference && doc.clause_reference.length > 0 ? (
                    <span
                      className={styles.clickableClause}
                      onClick={() =>
                        handleClauseClick(
                          doc.document_name,
                          doc.clause_reference,
                        )
                      }
                      title="Click to view clause details"
                    >
                      {doc.clause_reference
                        .map((ref) => ref.clause_number)
                        .filter(Boolean)
                        .join(", ") || "View Details"}
                    </span>
                  ) : (
                    "N/A"
                  )}
                </td>
                <td className={styles.centered}>
                  <Tooltip title={doc.reason || "N/A"} arrow placement="top">
                    <span className={styles.triggerBadge}>
                      {doc.trigger_result}
                    </span>
                  </Tooltip>
                </td>
                <td className={styles.actionRequired}>
                  {Array.isArray(doc.action_required) ? (
                    <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                      {doc.action_required.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    doc.action_required
                  )}
                </td>
                <td className={styles.actionRequired}>
                  {doc.consequence_summary || "N/A"}
                </td>
                <td className={styles.centered}>
                  <span
                    className={`${styles.riskBadge} ${
                      styles[getRiskTierColor(doc.risk_tier)]
                    }`}
                  ></span>
                </td>
                {isLatestMessage && (
                  <td className={styles.feedbackCell}>
                    <div className={styles.feedbackContainer}>
                      <Select
                        size="small"
                        displayEmpty
                        value={feedbackData[index]?.feedback_type || ""}
                        onChange={(e) =>
                          handleFeedbackChange(
                            index,
                            "feedback_type",
                            e.target.value,
                          )
                        }
                        disabled={feedbackSubmitted}
                        className={styles.feedbackDropdown}
                        sx={{
                          fontSize: "13px",
                          marginBottom: "8px",
                          backgroundColor: feedbackSubmitted ? "#f5f5f5" : "#fff",
                          "& .MuiSelect-select": {
                            padding: "8px 12px",
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>Select feedback type</em>
                        </MenuItem>
                        <MenuItem value="Useful">Useful</MenuItem>
                        <MenuItem value="Too generic">Too generic</MenuItem>
                        <MenuItem value="Incorrect">Incorrect</MenuItem>
                      </Select>
                      <TextField
                        size="small"
                        multiline
                        minRows={2}
                        maxRows={4}
                        placeholder="Enter your feedback..."
                        value={feedbackData[index]?.feedback_comment || ""}
                        onChange={(e) =>
                          handleFeedbackChange(
                            index,
                            "feedback_comment",
                            e.target.value,
                          )
                        }
                        disabled={feedbackSubmitted}
                        className={styles.feedbackTextbox}
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            fontSize: "13px",
                            backgroundColor: feedbackSubmitted ? "#f5f5f5" : "#fff",
                          },
                        }}
                      />
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Question Section */}
      {parsedContent?.question && (
        <Box className={styles.questionBox}>
          <Box className={styles.questionContent}>
            <MarkdownRenderer content={parsedContent.question} />
            {isLatestMessage && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid #e8e8e8",
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CheckCircleOutlineIcon />}
                  onClick={() => handleConfirmation("yes")}
                  disabled={feedbackSubmitted}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#00a888",
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: "#00947a" },
                    "&.Mui-disabled": {
                      backgroundColor: "#e0e0e0",
                      color: "#9e9e9e",
                    },
                  }}
                >
                  Yes, I'm satisfied
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleConfirmation("no")}
                  disabled={feedbackSubmitted}
                  sx={{
                    textTransform: "none",
                    borderColor: "#00a888",
                    color: "#00a888",
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: "#00947a",
                      backgroundColor: "rgba(0, 168, 136, 0.04)",
                    },
                    "&.Mui-disabled": {
                      borderColor: "#e0e0e0",
                      color: "#9e9e9e",
                    },
                  }}
                >
                  No, I'd like to modify
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Clause Details Modal */}
      <Dialog
        open={Boolean(modalData)}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pr: 6 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: 600, color: "#1a1a1a" }}
          >
            Clause References
          </Typography>
          {modalData?.documentName && (
            <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
              Document: {modalData.documentName}
            </Typography>
          )}
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#666",
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {modalData?.clauses?.map((clause, index) => (
            <Box
              key={index}
              sx={{ mb: index < modalData.clauses.length - 1 ? 3 : 0 }}
            >
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1 }}>
                    {clause.clause_number && (
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "#1a1a1a", mb: 0.5 }}
                      >
                        Clause {clause.clause_number}
                      </Typography>
                    )}
                    {clause.section_reference && (
                      <Typography
                        variant="body2"
                        sx={{ color: "#00a888", fontWeight: 500 }}
                      >
                        Section: {clause.section_reference}
                      </Typography>
                    )}
                  </Box>
                  {clause.clause_document_url && (
                    <Tooltip title="View source document" arrow>
                      <IconButton
                        size="small"
                        onClick={() => window.open(clause.clause_document_url, '_blank')}
                        sx={{
                          color: "#00a888",
                          "&:hover": { backgroundColor: "rgba(0, 168, 136, 0.08)" },
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              {clause.clause_text_snippet && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#f9f9f9",
                    borderRadius: 1,
                    borderLeft: "3px solid #00a888",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#555",
                      fontStyle: "italic",
                      lineHeight: 1.6,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    "{clause.clause_text_snippet}"
                  </Typography>
                </Box>
              )}
              {index < modalData.clauses.length - 1 && (
                <Divider sx={{ mt: 3 }} />
              )}
            </Box>
          ))}
          {(!modalData?.clauses || modalData.clauses.length === 0) && (
            <Typography
              variant="body2"
              sx={{ color: "#999", textAlign: "center", py: 2 }}
            >
              No clause details available
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
